import Login from "pages/login";
import Profile from "pages/profile";
import Register from "pages/register";
import AudioCheck from "pages/audio-check";
import OnboardingAudioCheck from "pages/onboarding-audio-check";
import SetupProfilePage from "pages/setup-profile";
import CreateNotes from "pages/create-notes";
import MySessions from "pages/my-sessions";
import Templates from "pages/templates";
import Subscription from "pages/subscription";
import TutorialSection from "pages/tutorial";
import useSidebar from "zustand-store/sidebar";
import withAuth from "utils/hoc/protected-route";
import ForgotPassword from "pages/forgot-password";
import OnboardingRedirect from "components/onboarding-redirect";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import getContentWrapperClasses from "utils/methods/getContentWrapperClasses";

const ProtectedProfile = withAuth(Profile);
const ProtectedAudioCheck = withAuth(AudioCheck);
const ProtectedOnboardingAudioCheck = withAuth(OnboardingAudioCheck);
const ProtectedSetupProfile = withAuth(SetupProfilePage);
const ProtectedCreateNotes = withAuth(CreateNotes);
const ProtectedSubscription = withAuth(Subscription);
const ProtectedTutorial = withAuth(TutorialSection);
const ProtectedTMySessions = withAuth(MySessions);
const ProtectedTemplates = withAuth(Templates);

const Content = () => {
  const location = useLocation();
  const { isOpen } = useSidebar();

  // CreateNotes is rendered outside of Routes and kept permanently mounted.
  // Hiding it with CSS (instead of unmounting) means:
  //   1. An active WebSocket recording continues uninterrupted while the user
  //      browses other tabs — microphone and transcription stay alive.
  //   2. liveUtterances (transcript blocks + timestamps) are preserved in
  //      component state, so returning to the tab shows exactly what was there.
  // Not mounted on public routes — withAuth inside would trigger <Navigate to="/login">
  // even when hidden, bouncing unauthenticated users away from /register etc.
  const publicRoutes = ["/login", "/register", "/forgot-password"];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isCreateNotes = location.pathname === "/create-notes" || location.pathname === "/";

  return (
    <div className={`${getContentWrapperClasses(isOpen, location)}`}>

      {/* App-level gate: keeps a half-onboarded user inside the onboarding flow */}
      <OnboardingRedirect />

      {/* Always-mounted CreateNotes — never unmounts during sidebar navigation */}
      {!isPublicRoute && (
        <div style={{ display: isCreateNotes ? "" : "none" }}>
          <ProtectedCreateNotes />
        </div>
      )}

      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Navigate to="/create-notes" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Protected Routes */}
        <Route path="/profile" element={<ProtectedProfile />} />
        <Route path="/audio-check" element={<ProtectedAudioCheck />} />
        <Route path="/onboarding-audio-check" element={<ProtectedOnboardingAudioCheck />} />
        <Route path="/setup-profile" element={<ProtectedSetupProfile />} />
        {/* /create-notes is handled by the always-mounted component above — no Route needed */}
        <Route path="/my-sessions" element={<ProtectedTMySessions />} />
        <Route path="/templates" element={<ProtectedTemplates />} />
        <Route path="/subscription" element={<ProtectedSubscription />} />
        <Route path="/tutorial" element={<ProtectedTutorial />} />
      </Routes>
    </div>
  );
};

export default Content;